import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { bookingsService, roomsService, availabilityService, coursesService, addonsService, omgExperienceService } from '../services/api';
import { format, addDays, differenceInDays } from 'date-fns';
import { CounterInput } from './ui';
import SuperAdminPastDateReservation from './SuperAdminPastDateReservation';
import { useIsMobile } from '../hooks';
import { BedDouble as BedDoubleIcon, Calendar as CalendarIcon, Flower2 as Flower2Icon, Home as HomeIcon, Lightbulb as LightbulbIcon, Palmtree as PalmtreeIcon, RefreshCw as RefreshCwIcon, Shield as ShieldIcon, Sparkles as SparklesIcon } from 'lucide-react';

function StaffReservation({ user, onReservationCreated }) {
  const isMobile = useIsMobile();
  // Check if user is super admin
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  
  // State to toggle between regular and past date booking
  const [isPastDateMode, setIsPastDateMode] = useState(false);
  
  // State to track OMG Experience mode
  const [isOMGExperienceMode, setIsOMGExperienceMode] = useState(false);
  
  const [currentStep, setCurrentStep] = useState('course-selection'); // 'course-selection', 'room-selection', 'guest-details'
  
  // Add CSS for spinner animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Form data state for course and dates
  const [formData, setFormData] = useState({
    checkInDate: '',
    checkOutDate: '',
    courseId: '',
    courseCount: 1,
    guestCount: 1,
    addons: [],
    notes: '',
    paymentStatus: 'PENDING'
  });

  // Multiple room selections with guest details
  const [selectedRooms, setSelectedRooms] = useState([]); // Array of { room, bed, guest: { name, email, phone, gender } }
  const [guestDetails, setGuestDetails] = useState([]); // Array of guest details for each room
  const [sameGuestForAll, setSameGuestForAll] = useState(true);
  const [primaryGuest, setPrimaryGuest] = useState({
    name: '',
    email: '',
    phone: '',
    gender: ''
  });

  // Data states
  const [courses, setCourses] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Pricing and calculation states
  const [totalPricing, setTotalPricing] = useState({
    individualBookings: [],
    totalAmount: 0,
    courseFee: 0,
    addonsTotal: 0
  });
  const [totalAmountChanged, setTotalAmountChanged] = useState(false);

  // Flash effect when total amount changes
  React.useEffect(() => {
    if (totalPricing.totalAmount > 0) {
      setTotalAmountChanged(true);
      const timer = setTimeout(() => setTotalAmountChanged(false), 500);
      return () => clearTimeout(timer);
    }
  }, [totalPricing.totalAmount]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);

  const mobileFieldStyle = {
    width: '100%',
    padding: isMobile ? '10px 12px' : '10px',
    border: '1px solid #D2CDBF',
    borderRadius: '8px',
    fontSize: isMobile ? '14px' : '14px',
    boxSizing: 'border-box'
  };

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [coursesData, addonsData] = await Promise.all([
        coursesService.getAll({ 
          sortBy: 'createdAt', 
          sortOrder: 'desc',
          limit: 100 
        }),
        addonsService.getAll()
      ]);

      // Fix courses data extraction - get courses from data.courses
      const coursesArray = coursesData.data?.courses || coursesData.courses || [];
      setCourses(Array.isArray(coursesArray) ? coursesArray : []);
      
      // Fix addons data extraction - get addons from data.addons or direct array
      const addonsArray = addonsData.data?.addons || addonsData.data || addonsData || [];
      setAddons(Array.isArray(addonsArray) ? addonsArray : []);
      
      console.log('Loaded initial data:', {
        coursesCount: coursesArray.length,
        addonsCount: addonsArray.length,
        courses: coursesArray.map(c => ({ 
          id: c.id, 
          name: c.name, 
          type: c.startDate && c.endDate ? 'fixed' : 'duration',
          startDate: c.startDate,
          endDate: c.endDate,
          duration: c.duration
        }))
      });
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load form data');
      setCourses([]);
      setAddons([]);
    }
  };

  // Calculate price when relevant data changes
  const calculateMultipleBookingsPricing = useCallback(async () => {
    if (!formData.checkInDate || !formData.checkOutDate || selectedRooms.length === 0) {
      setTotalPricing({
        individualBookings: [],
        totalAmount: 0,
        courseFee: 0,
        addonsTotal: 0
      });
      return;
    }

    setCalculatingPrice(true);
    try {
      // NEW API FORMAT: Send all rooms and beds in ONE request
      const checkInISO = new Date(formData.checkInDate + 'T15:00:00.000Z').toISOString();
      const checkOutISO = new Date(formData.checkOutDate + 'T11:00:00.000Z').toISOString();
      
      // Separate rooms and beds
      const rooms = [];
      const beds = [];
      
      selectedRooms.forEach((selection) => {
        if (selection.bed?.id) {
          // Shared room - add to beds array
          beds.push({
            bedId: selection.bed.id,
            checkIn: checkInISO,
            checkOut: checkOutISO
          });
        } else {
          // Private room - add to rooms array
          rooms.push({
            roomId: selection.room.id,
            checkIn: checkInISO,
            checkOut: checkOutISO
          });
        }
      });

      // Build price calculation request in NEW format
      const priceData = {
        rooms: rooms,
        beds: beds,
        courses: formData.courseId ? [{
          courseId: formData.courseId,
          quantity: formData.courseCount || 1
        }] : [],
        addOns: formData.addons.map(addonId => ({
          addOnServiceId: addonId,
          quantity: 1
        })),
        isInterState: false // You can make this dynamic if needed
      };

      console.log('Calculating price with NEW format:', {
        roomCount: rooms.length,
        bedCount: beds.length,
        courseCount: priceData.courses.length,
        addOnCount: priceData.addOns.length,
        isOMGMode: isOMGExperienceMode
      });

      // Use OMG Experience API if in OMG mode
      let response;
      if (isOMGExperienceMode) {
        console.log('Using OMG Experience pricing endpoint');
        response = await omgExperienceService.calculatePrice(priceData);
      } else {
        response = await bookingsService.calculatePrice(priceData);
      }
      const pricing = response.data;
      
      console.log('Pricing response:', pricing);
      console.log('Is OMG Mode:', isOMGExperienceMode);
      
      // NEW UNIFIED FORMAT: { totalPrice, breakdown: { rooms, beds, courses, addOns }, pricingType, hasOMGMarkup }
      const breakdown = pricing.breakdown || {};
      const totalAmount = pricing.totalPrice || 0;
      const pricingType = pricing.pricingType || 'regular';
      const hasOMGMarkup = pricing.hasOMGMarkup || false;
      
      // Calculate course fee from breakdown
      const courseFee = breakdown.courses?.reduce((sum, course) => 
        sum + (course.total || course.fee || 0), 0) || 0;
      
      // Calculate addons total
      const addonsTotal = breakdown.addOns?.reduce((sum, addon) => 
        sum + (addon.total || 0), 0) || 0;
      
      // Store breakdown for each room/bed with proper structure
      const individualBreakdowns = selectedRooms.map((selection) => {
        if (selection.bed?.id) {
          // Find bed breakdown
          const bedBreakdown = breakdown.beds?.find(b => b.bedId === selection.bed.id);
          if (bedBreakdown) {
            return {
              ...selection,
              pricing: {
                accommodationPrice: bedBreakdown.total || 0,
                basePrice: bedBreakdown.basePrice || 0,
                omgMarkup: bedBreakdown.omgMarkup || 0,
                nights: bedBreakdown.nights || 0,
                pricePerNight: bedBreakdown.nights > 0 ? Math.round(bedBreakdown.basePrice / bedBreakdown.nights) : 0,
                dailyPrices: bedBreakdown.dailyPrices || [],
                isOMGPricing: bedBreakdown.isOMGPricing || false,
                addOnsPrice: 0
              }
            };
          }
        } else {
          // Find room breakdown
          const roomBreakdown = breakdown.rooms?.find(r => r.roomId === selection.room.id);
          if (roomBreakdown) {
            return {
              ...selection,
              pricing: {
                accommodationPrice: roomBreakdown.total || 0,
                basePrice: roomBreakdown.basePrice || 0,
                omgMarkup: roomBreakdown.omgMarkup || 0,
                nights: roomBreakdown.nights || 0,
                pricePerNight: roomBreakdown.nights > 0 ? Math.round(roomBreakdown.basePrice / roomBreakdown.nights) : 0,
                dailyPrices: roomBreakdown.dailyPrices || [],
                isOMGPricing: roomBreakdown.isOMGPricing || false,
                addOnsPrice: 0
              }
            };
          }
        }
        // Fallback if not found
        return {
          ...selection,
          pricing: {
            accommodationPrice: 0,
            basePrice: 0,
            omgMarkup: 0,
            nights: 0,
            pricePerNight: 0,
            addOnsPrice: 0
          }
        };
      });
      
      console.log('Parsed pricing:', {
        totalAmount,
        courseFee,
        addonsTotal,
        breakdownCount: individualBreakdowns.length,
        pricingType,
        hasOMGMarkup
      });
      
      // Calculate totals for display
      const totalBasePrice = individualBreakdowns.reduce((sum, item) => 
        sum + (item.pricing?.basePrice || 0), 0);
      const totalOMGMarkup = individualBreakdowns.reduce((sum, item) => 
        sum + (item.pricing?.omgMarkup || 0), 0);
      const totalAccommodation = individualBreakdowns.reduce((sum, item) => 
        sum + (item.pricing?.accommodationPrice || 0), 0);
      
      setTotalPricing({
        individualBookings: individualBreakdowns,
        totalAmount: totalAmount,
        courseFee: courseFee,
        addonsTotal: addonsTotal,
        totalBasePrice: totalBasePrice,
        totalOMGMarkup: totalOMGMarkup,
        totalAccommodation: totalAccommodation,
        pricingType: pricingType,
        hasOMGMarkup: hasOMGMarkup,
        markupInfo: pricing.markupInfo || '',
        fullPricing: pricing // Store complete pricing data
      });
    } catch (error) {
      console.error('Error calculating pricing:', error);
      toast.error('Failed to calculate pricing');
      setTotalPricing({
        individualBookings: [],
        totalAmount: 0,
        courseFee: 0,
        addonsTotal: 0
      });
    } finally {
      setCalculatingPrice(false);
    }
  }, [formData.checkInDate, formData.checkOutDate, selectedRooms, formData.courseId, formData.courseCount, formData.addons]);

  // Load available rooms based on dates and course
  const loadAvailableRooms = useCallback(async () => {
    if (!formData.checkInDate || !formData.checkOutDate) {
      setAvailableRooms([]);
      return;
    }

    setLoadingRooms(true);
    try {
      // Get all rooms first
      const roomsResponse = await roomsService.getAll({ limit: 100 });
      const allRooms = roomsResponse.rooms || roomsResponse.data?.rooms || [];

      // Check availability for each room and bed
      const availabilityParams = {
        checkIn: new Date(formData.checkInDate).toISOString(),
        checkOut: new Date(formData.checkOutDate).toISOString(),
      };

      const roomsWithAvailability = [];

      for (const room of allRooms) {
        if (room.status !== 'AVAILABLE') continue;

        const roomData = { ...room, availableBeds: [] };

        // Check if it's a private room
        const isPrivate = !room.roomType?.isShared;

        if (isPrivate) {
          // Check room availability for private rooms
          try {
            const roomAvailability = await availabilityService.checkRoom(room.id, availabilityParams);
            if (roomAvailability?.data?.isAvailable) {
              roomData.isAvailable = true;
              roomsWithAvailability.push(roomData);
            }
          } catch (error) {
            console.warn(`Could not check availability for room ${room.id}:`, error);
          }
        } else {
          // Check bed availability for shared rooms
          if (room.beds && room.beds.length > 0) {
            for (const bed of room.beds) {
              if (!bed.isAvailable) continue;

              try {
                const bedAvailability = await availabilityService.checkBed(bed.id, availabilityParams);
                if (bedAvailability?.data?.isAvailable) {
                  roomData.availableBeds.push(bed);
                }
              } catch (error) {
                console.warn(`Could not check availability for bed ${bed.id}:`, error);
              }
            }

            if (roomData.availableBeds.length > 0) {
              roomsWithAvailability.push(roomData);
            }
          }
        }
      }

      console.log('Loaded available rooms:', {
        totalRooms: roomsWithAvailability.length,
        roomDetails: roomsWithAvailability.map(r => ({
          id: r.id,
          number: r.roomNumber,
          type: r.roomType?.name,
          isShared: r.roomType?.isShared,
          availableBeds: r.availableBeds?.length || 0
        })),
        isOMGMode: isOMGExperienceMode
      });
      
      // If in OMG mode, log which rooms are OMG-eligible
      if (isOMGExperienceMode) {
        const omgEligibleRooms = roomsWithAvailability.filter(room => 
          omgExperienceService.isSharedRoomType(room.roomType?.name)
        );
        console.log('OMG Eligible Rooms:', {
          count: omgEligibleRooms.length,
          rooms: omgEligibleRooms.map(r => ({
            number: r.roomNumber,
            type: r.roomType?.name
          }))
        });
      }
      
      setAvailableRooms(roomsWithAvailability);
    } catch (error) {
      console.error('Error loading available rooms:', error);
      toast.error('Failed to load available rooms');
      setAvailableRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  }, [formData.checkInDate, formData.checkOutDate, isOMGExperienceMode]);

  // Handle OMG Experience selection
  const handleOMGExperienceSelect = () => {
    console.log('OMG Experience Mode Activated');
    setIsOMGExperienceMode(true);
    setSelectedCourse(null);
    setFormData(prev => ({
      ...prev,
      courseId: '',
      courseCount: 0,
      checkInDate: '',
      checkOutDate: ''
    }));
    setCurrentStep('room-selection');
    toast.info('OMG Experience mode activated! Select Daya or Meera Sharing rooms.', {
      autoClose: 4000
    });
  };

  // Handle course selection
  const handleCourseSelect = (course) => {
    console.log('Staff Reservation Course Selection:', {
      selectedCourse: course ? { id: course.id, name: course.name, fee: course.fee } : null,
      isStaycation: !course
    });

    // Reset OMG Experience mode when selecting regular course/staycation
    setIsOMGExperienceMode(false);

    if (course) {
      setSelectedCourse(course);
      
      // Handle both fixed date and duration-based courses
      if (course.startDate && course.endDate) {
        // Fixed date course - set dates automatically
        setFormData(prev => ({
          ...prev,
          courseId: course.id,
          courseCount: 1, // Reset to 1 for course selections
          checkInDate: course.startDate.split('T')[0],
          checkOutDate: course.endDate.split('T')[0]
        }));
        console.log('Fixed date course selected, dates set to:', {
          courseId: course.id,
          courseCount: 1,
          checkIn: course.startDate.split('T')[0],
          checkOut: course.endDate.split('T')[0]
        });
      } else if (course.duration) {
        // Duration-based course - don't set dates, user will choose them
        setFormData(prev => ({
          ...prev,
          courseId: course.id,
          courseCount: 1, // Reset to 1 for course selections
          checkInDate: '',
          checkOutDate: ''
        }));
        console.log('Duration-based course selected, user will choose dates:', {
          courseId: course.id,
          courseCount: 1,
          duration: course.duration
        });
        // Notify staff they'll need to set dates manually
        toast.info(`Duration-based course selected. You'll set custom dates in the next step (max ${course.duration} days).`, {
          autoClose: 4000
        });
      }
    } else {
      // Staycation case - no course selected, set course count to 0
      setSelectedCourse(null);
      setFormData(prev => ({
        ...prev,
        courseId: '',
        courseCount: 0, // Always 0 for staycation
        checkInDate: '',
        checkOutDate: ''
      }));
      console.log('Staycation mode - no course selected, course count set to 0');
    }
    setCurrentStep('room-selection');
  };

  // Handle room/bed selection (multiple selections)
  const handleAccommodationSelect = (room, bed = null) => {
    const selectionKey = bed ? `bed-${bed.id}` : `room-${room.id}`;
    
    // Check if already selected
    const alreadySelected = selectedRooms.some(sel => 
      bed ? sel.bed?.id === bed.id : (sel.room.id === room.id && !sel.bed)
    );
    
    if (alreadySelected) {
      toast.info('Already added to selection');
      return;
    }

    const newSelection = { id: selectionKey, room, bed };
    setSelectedRooms(prev => [...prev, newSelection]);
    
    // Initialize guest details for new selection
    setGuestDetails(prev => ([
      ...prev,
      {
        name: sameGuestForAll ? (primaryGuest.name || '') : '',
        email: sameGuestForAll ? (primaryGuest.email || '') : '',
        phone: sameGuestForAll ? (primaryGuest.phone || '') : '',
        gender: bed?.isFemaleOnly ? 'FEMALE' : (sameGuestForAll ? (primaryGuest.gender || '') : '')
      }
    ]));
    
    toast.success(`Added ${bed ? `Bed ${bed.bedNumber}` : `Room ${room.roomNumber}`}`);
  };

  // Remove room selection
  const removeRoomSelection = (index) => {
    setSelectedRooms(prev => prev.filter((_, i) => i !== index));
    setGuestDetails(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-calculate checkout date for duration-based courses
    if (name === 'checkInDate' && selectedCourse && selectedCourse.duration && !selectedCourse.startDate) {
      const checkInDate = new Date(value);
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + selectedCourse.duration);
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        checkOutDate: checkOutDate.toISOString().split('T')[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle guest details changes
  const handleGuestDetailsChange = (index, field, value) => {
    console.log(`Guest details change: Guest ${index + 1}, ${field} = "${value}"`);
    setGuestDetails(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      console.log(`Updated guestDetails[${index}]:`, updated[index]);
      return updated;
    });
  };

  // Handle primary guest details changes
  const handlePrimaryGuestChange = (field, value) => {
    setPrimaryGuest(prev => {
      const updated = { ...prev, [field]: value };
      if (sameGuestForAll) {
        setGuestDetails(prevGuests => prevGuests.map(g => ({
          ...g,
          [field]: value
        })));
      }
      return updated;
    });
  };

  // Toggle same guest for all
  const handleSameGuestToggle = (checked) => {
    setSameGuestForAll(checked);
    if (checked) {
      setGuestDetails(prev => prev.map(g => ({
        name: primaryGuest.name || g.name || '',
        email: primaryGuest.email || g.email || '',
        phone: primaryGuest.phone || g.phone || '',
        gender: primaryGuest.gender || g.gender || ''
      })));
    }
  };

  const proceedToGuestDetails = () => {
    if (selectedRooms.length === 0) {
      toast.error('Please add at least one room/bed');
      return;
    }
    
    console.log('Proceeding to guest details:', {
      selectedRoomsCount: selectedRooms.length,
      guestDetailsCount: guestDetails.length,
      selectedRooms: selectedRooms,
      guestDetails: guestDetails
    });
    
    setCurrentStep('guest-details');
    calculateMultipleBookingsPricing();
  };

  // Handle addon changes
  const handleAddonChange = (addonId, checked) => {
    setFormData(prev => ({
      ...prev,
      addons: checked 
        ? [...prev.addons, addonId]
        : prev.addons.filter(id => id !== addonId)
    }));
  };

  // Handle form submission - create multiple bookings
  const handleSubmit = async (e) => {
    if (e) e.preventDefault(); // Handle both form submit and button click
    
    // Small delay to ensure all state updates are processed
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Validation
    if (!formData.checkInDate || !formData.checkOutDate) {
      toast.error('Please set check-in and check-out dates');
      return;
    }

    if (selectedRooms.length === 0) {
      toast.error('Please select at least one accommodation');
      return;
    }

    // Validate guest details - check against selectedRooms length
    console.log('Debug guest validation:', {
      selectedRoomsCount: selectedRooms.length,
      guestDetailsCount: guestDetails.length,
      guestDetails: guestDetails,
      sameGuestForAll: sameGuestForAll,
      primaryGuest: primaryGuest
    });

    // Ensure arrays are synchronized
    let validationGuestDetails = guestDetails;
    if (guestDetails.length !== selectedRooms.length) {
      console.warn('Arrays not synchronized, fixing...');
      validationGuestDetails = selectedRooms.map((_, index) => 
        guestDetails[index] || {
          name: sameGuestForAll ? (primaryGuest.name || '') : '',
          email: sameGuestForAll ? (primaryGuest.email || '') : '',
          phone: sameGuestForAll ? (primaryGuest.phone || '') : '',
          gender: sameGuestForAll ? (primaryGuest.gender || '') : ''
        }
      );
      setGuestDetails(validationGuestDetails);
    }

    // Use primary guest for booking (first guest)
    const primaryGuestData = validationGuestDetails[0];
    if (!primaryGuestData || !primaryGuestData.name || !primaryGuestData.email || !primaryGuestData.gender) {
      toast.error('Please fill in all required fields for the primary guest (Name, Email, Gender)');
      return;
    }

    for (let i = 0; i < selectedRooms.length; i++) {
      const guest = validationGuestDetails[i];
      console.log(`Validating Guest ${i + 1}:`, guest);
      
      if (!guest || !guest.name || !guest.email || !guest.gender) {
        console.error(`Guest ${i + 1} validation failed:`, {
          hasGuest: !!guest,
          name: guest?.name,
          email: guest?.email,
          gender: guest?.gender
        });
        toast.error(`Please fill in all required fields for Guest ${i + 1} (Name, Email, Gender)`);
        return;
      }
      // Female-only bed enforcement
      const sel = selectedRooms[i];
      if (sel?.bed?.isFemaleOnly && guest.gender !== 'FEMALE') {
        toast.error(`Guest ${i + 1} must be Female for the selected female-only bed.`);
        return;
      }
    }

    setLoading(true);
    try {
      // NEW API FORMAT: Create ONE booking with multiple rooms and beds
      const checkInISO = new Date(formData.checkInDate + 'T15:00:00.000Z').toISOString();
      const checkOutISO = new Date(formData.checkOutDate + 'T11:00:00.000Z').toISOString();
      
      // Separate rooms and beds
      const rooms = [];
      const beds = [];
      
      selectedRooms.forEach((selection, index) => {
        const guest = validationGuestDetails[index];
        const notes = [];
        
        // Add bed number to notes if applicable
        if (selection.bed && selection.bed.bedNumber) {
          notes.push(`Bed ${selection.bed.bedNumber}`);
        }
        
        // Add guest info to notes
        notes.push(`Guest: ${guest.name} (${guest.email})`);
        
        // Add general notes if this is the first selection
        if (index === 0 && formData.notes) {
          notes.push(formData.notes);
        }
        
        if (selection.bed?.id) {
          // Shared room - add to beds array
          beds.push({
            bedId: selection.bed.id,
            checkIn: checkInISO,
            checkOut: checkOutISO,
            notes: notes.join(' | ')
          });
        } else {
          // Private room - add to rooms array
          rooms.push({
            roomId: selection.room.id,
            checkIn: checkInISO,
            checkOut: checkOutISO,
            notes: notes.join(' | ')
          });
        }
      });

      // Build reservation data in NEW format
      const reservationData = {
        guestDetails: {
          name: primaryGuestData.name,
          email: primaryGuestData.email,
          phone: primaryGuestData.phone || null,
          gender: primaryGuestData.gender
        },
        checkIn: checkInISO,
        checkOut: checkOutISO,
        guestCount: formData.guestCount || 1,
        rooms: rooms,
        beds: beds,
        courses: formData.courseId ? [{
          courseId: formData.courseId,
          quantity: formData.courseCount || 1,
          notes: null
        }] : [],
        addOns: formData.addons.map(addonId => ({
          addOnServiceId: addonId,
          quantity: 1
        })),
        notes: formData.notes || `Multi-accommodation booking: ${rooms.length} room(s), ${beds.length} bed(s)`,
        bookingPlatform: 'STAFF_RESERVATION'
      };

      console.log('Creating Staff Reservation (NEW FORMAT):', {
        ...reservationData,
        roomCount: rooms.length,
        bedCount: beds.length,
        courseCount: reservationData.courses.length,
        isStaycation: reservationData.courses.length === 0,
        isOMGMode: isOMGExperienceMode
      });

      // Use OMG Experience API if in OMG mode
      if (isOMGExperienceMode) {
        console.log('Using OMG Experience staff booking endpoint');
        await omgExperienceService.createStaffReservation(reservationData);
        toast.success(`Successfully created OMG Experience reservation with 20% markup! ${rooms.length} room(s) and ${beds.length} bed(s).`, {
          autoClose: 5000
        });
      } else {
        await bookingsService.createStaffReservation(reservationData);
        toast.success(`Successfully created staff reservation with ${rooms.length} room(s) and ${beds.length} bed(s)!`);
      }
      
      // Reset form
      setFormData({
        checkInDate: '',
        checkOutDate: '',
        courseId: '',
        courseCount: 1,
        guestCount: 1,
        addons: [],
        notes: '',
        paymentStatus: 'PENDING'
      });
      
      setSelectedRooms([]);
      setGuestDetails([]);
      setPrimaryGuest({ name: '', email: '', phone: '', gender: '' });
      setSameGuestForAll(true);
      setSelectedCourse(null);
      setIsOMGExperienceMode(false);
      setTotalPricing({ individualBookings: [], totalAmount: 0, courseFee: 0, addonsTotal: 0 });
      setCurrentStep('course-selection');
      
      if (onReservationCreated) {
        onReservationCreated();
      }
    } catch (error) {
      console.error('Error creating staff reservation:', error);
      
      // Enhanced error handling with specific messages
      if (error.response?.status === 409) {
        toast.error(
          `Booking Conflict: One or more selected accommodations are already booked for these dates. Please refresh and select different accommodations.`,
          { autoClose: 8000 }
        );
        
        // Auto-refresh room availability to show current state
        setTimeout(() => {
          loadAvailableRooms();
        }, 1000);
        
        // Go back to room selection to choose alternative
        setCurrentStep('room-selection');
      } else if (error.response?.status === 400) {
        toast.error(`Validation Error: ${error.response?.data?.error || error.message}`);
      } else {
        toast.error(`Failed to create reservation: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load rooms when dates change
  useEffect(() => {
    if (currentStep === 'room-selection' && formData.checkInDate && formData.checkOutDate) {
      loadAvailableRooms();
    }
  }, [currentStep, formData.checkInDate, formData.checkOutDate, loadAvailableRooms]);

  // Calculate price when room selections change
  useEffect(() => {
    if (selectedRooms.length > 0 && formData.checkInDate && formData.checkOutDate) {
      calculateMultipleBookingsPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRooms, calculateMultipleBookingsPricing, formData.checkInDate, formData.checkOutDate]);

  const getMinDate = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const getMinCheckOutDate = () => {
    if (!formData.checkInDate) return getMinDate();
    return format(addDays(new Date(formData.checkInDate), 1), 'yyyy-MM-dd');
  };

  // Render course selection step
  const renderCourseSelection = () => (
    <div className="course-selection-step">
      <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
        <h2 style={{ color: '#36433B', borderBottom: '3px solid #1A3E2A', paddingBottom: '10px', fontSize: isMobile ? '22px' : '30px', lineHeight: 1.3 }}>
          Step 1: Select Course or Staycation
        </h2>
        <p style={{ color: '#5F6B61', marginTop: '10px' }}>
          Choose from fixed-date retreats, flexible duration courses, or skip for staycation
        </p>
      </div>

      <div style={{ display: 'grid', gap: isMobile ? '14px' : '20px', marginBottom: '30px' }}>
        {/* OMG Experience Option - NEW */}
        <div 
          style={{
            padding: '24px',
            padding: isMobile ? '18px' : '24px',
            border: '3px solid #A9613C',
            borderRadius: '12px',
            background: '#F8F0EA',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onClick={() => handleOMGExperienceSelect()}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#975534';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(169, 97, 60, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#A9613C';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {/* Badge */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: '#A9613C',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            +20% Premium
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', paddingRight: isMobile ? '72px' : '0' }}>
            <span style={{ fontSize: '28px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
            <h3 style={{ margin: 0, color: '#663923', fontWeight: '700' }}>OMG Experience</h3>
          </div>
          
          <p style={{ color: '#663923', margin: '0 0 12px 0', fontWeight: '500' }}>
            Premium shared room staycation with enhanced pricing
          </p>
          
          <div style={{ 
            backgroundColor: '#ffffff', 
            padding: '12px', 
            borderRadius: '8px',
            border: '1px solid #E4C2AC'
          }}>
            <div style={{ fontSize: '13px', color: '#975534', marginBottom: '8px' }}>
              <strong>Includes:</strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#663923', fontSize: '13px' }}>
              <li>Daya Sharing & Meera Sharing rooms only</li>
              <li>20% premium markup on accommodation</li>
              <li>No course required - flexible dates</li>
              <li>Enhanced staycation experience</li>
            </ul>
          </div>
        </div>

        {/* Staycation Option */}
        <div 
          style={{
            padding: isMobile ? '18px' : '24px',
            border: '2px solid #A13F2F',
            borderRadius: '12px',
            backgroundColor: '#F9EEEB',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => handleCourseSelect(null)}
          onMouseOver={(e) => e.target.style.borderColor = '#7E472C'}
          onMouseOut={(e) => e.target.style.borderColor = '#A13F2F'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '24px' }}><PalmtreeIcon size="1em" aria-hidden="true" /></span>
            <h3 style={{ margin: 0, color: '#36433B' }}>Staycation (No Course)</h3>
          </div>
          <p style={{ color: '#5F6B61', margin: 0 }}>
            Regular accommodation without course commitment. You'll set custom dates.
          </p>
        </div>

        {/* Available Courses */}
        {courses.length > 0 && courses.map(course => {
          const courseType = course.startDate && course.endDate ? 'fixed' : 'duration';
          let isAvailable = true;
          let statusText = '';
          
          if (courseType === 'fixed') {
            const startDate = new Date(course.startDate);
            isAvailable = startDate > new Date();
            statusText = isAvailable ? 'Upcoming' : 'Course has already started';
          } else {
            // Duration-based courses are always available
            statusText = 'Available year-round';
          }
          
          return (
            <div 
              key={course.id}
              style={{
                padding: isMobile ? '18px' : '24px',
                border: `2px solid ${isAvailable ? (courseType === 'fixed' ? '#1A3E2A' : '#1A3E2A') : '#A5A89B'}`,
                borderRadius: '12px',
                backgroundColor: isAvailable ? '#F5F3EB' : '#EEEBE1',
                cursor: isAvailable ? 'pointer' : 'not-allowed',
                opacity: isAvailable ? 1 : 0.6,
                transition: 'all 0.3s ease'
              }}
              onClick={() => isAvailable && handleCourseSelect(course)}
              onMouseOver={(e) => isAvailable && (e.target.style.borderColor = courseType === 'fixed' ? '#163626' : '#163626')}
              onMouseOut={(e) => isAvailable && (e.target.style.borderColor = courseType === 'fixed' ? '#1A3E2A' : '#1A3E2A')}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '24px' }}><Flower2Icon size="1em" aria-hidden="true" /></span>
                    <h3 style={{ margin: 0, color: '#36433B' }}>{course.name}</h3>
                    {/* Course Type Badge */}
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: courseType === 'fixed' ? '#E1E9E2' : '#E1E9E2',
                      color: courseType === 'fixed' ? '#0F2619' : '#163626',
                      fontWeight: '500'
                    }}>
                      {courseType === 'fixed' ? 'Fixed Date' : 'Duration-Based'}
                    </span>
                  </div>
                  <p style={{ color: '#5F6B61', margin: 0, fontSize: '14px' }}>
                    {course.description || 'Meditation and mindfulness retreat'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#163626' }}>
                    ₹{course.fee || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#5F6B61' }}>Course Fee</div>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                {courseType === 'fixed' ? (
                  <>
                    <div>
                      <strong>Start:</strong> {format(new Date(course.startDate), 'MMM dd, yyyy')}
                    </div>
                    <div>
                      <strong>End:</strong> {format(new Date(course.endDate), 'MMM dd, yyyy')}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <strong>Duration:</strong> {course.duration} days
                    </div>
                    <div>
                      <strong>Available:</strong> Year-round
                    </div>
                  </>
                )}
              </div>
              
              <div style={{ 
                marginTop: '12px', 
                fontSize: '12px', 
                fontWeight: 'bold',
                color: isAvailable ? (courseType === 'fixed' ? '#1A3E2A' : '#1A3E2A') : '#A13F2F'
              }}>
                {statusText}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render room selection step
  const renderRoomSelection = () => (
    <div className="room-selection-step" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 360px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ marginBottom: '30px' }}>
          <button 
            onClick={() => setCurrentStep('course-selection')}
            style={{
              backgroundColor: '#5F6B61',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Course Selection
          </button>
          
          <h2 style={{ color: '#36433B', borderBottom: '3px solid #1A3E2A', paddingBottom: '10px' }}>
            Step 2: Select Accommodation
          </h2>
          
          {selectedCourse ? (
            <div>
              <div style={{ backgroundColor: '#E1E9E2', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#163626' }}>Selected Course: {selectedCourse.name}</h4>
                {selectedCourse.startDate && selectedCourse.endDate ? (
                  <p style={{ margin: 0, color: '#5F6B61' }}>
                    {format(new Date(formData.checkInDate), 'MMM dd, yyyy')} - {format(new Date(formData.checkOutDate), 'MMM dd, yyyy')}
                  </p>
                ) : (
                  <p style={{ margin: 0, color: '#5F6B61' }}>
                    Duration-Based Course ({selectedCourse.duration} nights)
                  </p>
                )}
              </div>
              
              {/* Show date selection for duration-based courses */}
              {!selectedCourse.startDate && !selectedCourse.endDate && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#A13F2F', margin: '0 0 16px 0' }}>Set Your Check-in Date</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-in Date *</label>
                      <input
                        type="date"
                        name="checkInDate"
                        value={formData.checkInDate}
                        onChange={handleInputChange}
                        min={getMinDate()}
                        required
                        style={mobileFieldStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-out Date</label>
                      <input
                        type="date"
                        value={formData.checkOutDate}
                        disabled
                        style={{ 
                          ...mobileFieldStyle,
                          backgroundColor: '#EEEBE1',
                          color: '#5F6B61'
                        }}
                      />
                      <small style={{ color: '#5F6B61', fontSize: '12px' }}>
                        Auto-calculated based on course duration
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: '20px' }}>
              {/* OMG Experience Mode Indicator */}
              {isOMGExperienceMode && (
                <div style={{
                  backgroundColor: '#F1E0D4',
                  border: '2px solid #A9613C',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', color: '#663923', marginBottom: '4px' }}>
                      OMG Experience Mode Active
                    </div>
                    <div style={{ fontSize: '13px', color: '#975534' }}>
                      Premium 20% markup • Daya & Meera Sharing rooms only
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsOMGExperienceMode(false);
                      setCurrentStep('course-selection');
                    }}
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #A9613C',
                      color: '#A9613C',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
              
              <h4 style={{ color: isOMGExperienceMode ? '#A9613C' : '#A13F2F', margin: '0 0 16px 0' }}>
                {isOMGExperienceMode ? 'OMG Experience - Set Your Dates' : 'Staycation Mode - Set Your Dates'}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-in Date *</label>
                  <input
                    type="date"
                    name="checkInDate"
                    value={formData.checkInDate}
                    onChange={handleInputChange}
                    min={getMinDate()}
                    required
                    style={mobileFieldStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-out Date *</label>
                  <input
                    type="date"
                    name="checkOutDate"
                    value={formData.checkOutDate}
                    onChange={handleInputChange}
                    min={getMinCheckOutDate()}
                    required
                    style={mobileFieldStyle}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Available Rooms */}
        {loadingRooms ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '20px', marginBottom: '16px' }}><RefreshCwIcon size="1em" aria-hidden="true" /></div>
            <div>Loading available rooms...</div>
          </div>
        ) : availableRooms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#F3E6CB', borderRadius: '8px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
            <p>No rooms available for the selected dates. Please try different dates.</p>
          </div>
        ) : isOMGExperienceMode && availableRooms.filter(room => omgExperienceService.isSharedRoomType(room.roomType?.name)).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#F8F0EA', borderRadius: '8px', border: '2px solid #A9613C' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><SparklesIcon size="1em" aria-hidden="true" /></div>
            <h3 style={{ color: '#663923', marginBottom: '8px' }}>No OMG Experience Rooms Available</h3>
            <p style={{ color: '#975534', marginBottom: '16px' }}>
              Daya Sharing and Meera Sharing rooms are not available for the selected dates.
            </p>
            <button
              onClick={() => {
                setIsOMGExperienceMode(false);
                setCurrentStep('course-selection');
              }}
              style={{
                backgroundColor: '#A9613C',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Choose Different Option
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            {availableRooms
              .filter(room => {
                // In OMG Experience mode, show only Daya Sharing and Meera Sharing
                if (isOMGExperienceMode) {
                  return omgExperienceService.isSharedRoomType(room.roomType?.name);
                }
                // Otherwise show all rooms
                return true;
              })
              .map(room => {
              const isPrivate = !room.roomType?.isShared;
              const isOMGEligible = omgExperienceService.isSharedRoomType(room.roomType?.name);
              
              return (
                <div key={room.id} style={{
                  border: isOMGExperienceMode && isOMGEligible ? '2px solid #A9613C' : '1px solid #E3DFD3',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: isOMGExperienceMode && isOMGEligible ? '#F8F0EA' : 'white'
                }}>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, color: '#36433B' }}>
                            Room {room.roomNumber}
                          </h3>
                          {isOMGExperienceMode && isOMGEligible && (
                            <span style={{
                              backgroundColor: '#A9613C',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>
                              OMG
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#5F6B61', fontSize: '14px' }}>
                          {room.roomType?.name || 'Standard Room'}
                        </div>
                        {room.roomType?.description && (
                          <p style={{ color: '#5F6B61', fontSize: '14px', margin: '8px 0 0 0' }}>
                            {room.roomType.description}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {isOMGExperienceMode && isOMGEligible ? (
                          <>
                            <div style={{ fontSize: '14px', color: '#A5A89B', textDecoration: 'line-through', marginBottom: '4px' }}>
                              ₹{room.roomType?.basePrice || room.roomType?.defaultRate || 100}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#A9613C' }}>
                              ₹{Math.round((room.roomType?.basePrice || room.roomType?.defaultRate || 100) * 1.20)}/night
                            </div>
                            <div style={{ fontSize: '11px', color: '#A9613C', fontWeight: '600' }}>
                              +20% OMG Markup
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#163626' }}>
                              ₹{room.roomType?.basePrice || room.roomType?.defaultRate || 100}/night
                            </div>
                            <div style={{ fontSize: '12px', color: '#5F6B61' }}>Base Rate</div>
                          </>
                        )}
                      </div>
                    </div>

                    {isPrivate ? (
                      <button
                        onClick={() => handleAccommodationSelect(room)}
                        style={{
                          backgroundColor: '#1A3E2A',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          width: '100%',
                          fontWeight: 'bold'
                        }}
                      >
                        Add Private Room
                      </button>
                    ) : (
                      <div>
                        <h4 style={{ margin: '0 0 12px 0', color: '#36433B' }}>
                          Available Beds ({room.availableBeds.length})
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                          {room.availableBeds.map(bed => (
                            <button
                              key={bed.id}
                              onClick={() => handleAccommodationSelect(room, bed)}
                              style={{
                                padding: '16px 8px',
                                border: bed.isFemaleOnly ? '2px solid #8E3628' : '1px solid #E3DFD3',
                                borderRadius: '8px',
                                backgroundColor: bed.isFemaleOnly ? '#F2D9D3' : '#F5F3EB',
                                cursor: 'pointer',
                                textAlign: 'center',
                                fontSize: '12px'
                              }}
                            >
                              <div style={{ fontSize: '20px', marginBottom: '4px' }}><BedDoubleIcon size="1em" aria-hidden="true" /></div>
                              <div style={{ fontWeight: 'bold' }}>Bed {bed.bedNumber}</div>
                              {bed.isFemaleOnly && (
                                <div style={{ color: '#8E3628', fontSize: '10px', marginTop: '4px' }}>
                                  Female Only
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky Selection Sidebar */}
      <aside style={{ position: isMobile ? 'static' : 'sticky', top: 16, alignSelf: 'start', order: isMobile ? -1 : 0 }}>
        <div style={{ border: '1px solid #E3DFD3', borderRadius: 12, background: 'white', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1A3E2A' }}>Selected ({selectedRooms.length})</h3>
            {selectedRooms.length > 0 && (
              <button
                onClick={() => { setSelectedRooms([]); setGuestDetails([]); setTotalPricing({ individualBookings: [], totalAmount: 0, courseFee: 0, addonsTotal: 0 }); }}
                style={{ background: 'transparent', border: 'none', color: '#A13F2F', cursor: 'pointer', fontSize: 12 }}
                title="Clear all"
              >
                Clear
              </button>
            )}
          </div>

          {selectedRooms.length === 0 ? (
            <div style={{ color: '#5F6B61', fontSize: 13 }}>No selections yet</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {selectedRooms.map((sel, idx) => (
                <div key={sel.id} style={{ border: '1px solid #E3DFD3', borderRadius: 8, padding: 10, position: 'relative' }}>
                  <button
                    onClick={() => removeRoomSelection(idx)}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: '#A5A89B' }}
                    title="Remove"
                  >
                    ✕
                  </button>
                  <div style={{ fontSize: 13, color: '#1A3E2A', fontWeight: 600 }}>
                    {sel.bed ? `Room ${sel.room.roomNumber} • Bed ${sel.bed.bedNumber}` : `Room ${sel.room.roomNumber}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#5F6B61' }}>{sel.room.roomType?.name}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 12, borderTop: '1px solid #E3DFD3', paddingTop: 12 }}>
            <button
              onClick={proceedToGuestDetails}
              disabled={selectedRooms.length === 0}
              style={{
                width: '100%',
                backgroundColor: selectedRooms.length === 0 ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: selectedRooms.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 700
              }}
            >
              Continue →
            </button>
          </div>
        </div>
      </aside>
    </div>
  );

  // Render guest details + aggregated price step
  const renderGuestDetails = () => (
    <div className="guest-details-step" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ marginBottom: 20 }}>
          <button 
            onClick={() => setCurrentStep('room-selection')}
            style={{ backgroundColor: '#5F6B61', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
          >
            ← Back to Rooms
          </button>
        </div>

        {selectedCourse && (
          <div style={{ backgroundColor: '#E1E9E2', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: '#163626' }}>Course: {selectedCourse.name}</div>
            <div style={{ color: '#5F6B61' }}>
              Original Course Dates: {format(new Date(selectedCourse.startDate || formData.checkInDate), 'MMM dd, yyyy')} - {format(new Date(selectedCourse.endDate || formData.checkOutDate), 'MMM dd, yyyy')}
            </div>
          </div>
        )}

        {/* Date Extension Section - Allow booking beyond course dates */}
        {(selectedCourse || !isOMGExperienceMode) && (
          <div style={{
            backgroundColor: '#F0F3EE',
            border: '2px solid #1A3E2A',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}><CalendarIcon size="1em" aria-hidden="true" /></span>
              <h4 style={{ margin: 0, color: '#0F2619' }}>
                {selectedCourse ? 'Extend Stay Beyond Course Dates' : 'Adjust Your Dates'}
              </h4>
            </div>
            <p style={{ color: '#5F6B61', fontSize: 13, marginBottom: 12 }}>
              {selectedCourse 
                ? 'Book accommodation before course starts or after it ends. Price will adjust automatically.'
                : 'Modify your check-in and check-out dates. Price updates in real-time.'
              }
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                  Check-in Date *
                  {selectedCourse && selectedCourse.startDate && formData.checkInDate < selectedCourse.startDate && (
                    <span style={{ color: '#B68235', fontSize: 12, marginLeft: 8 }}>
                      (Early by {differenceInDays(new Date(selectedCourse.startDate), new Date(formData.checkInDate))} days)
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  name="checkInDate"
                  value={formData.checkInDate}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: 10,
                    border: '2px solid #1A3E2A',
                    borderRadius: 6,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
                {selectedCourse && selectedCourse.startDate && (
                  <div style={{ fontSize: 12, color: '#5F6B61', marginTop: 4 }}>
                    Course starts: {format(new Date(selectedCourse.startDate), 'MMM dd, yyyy')}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
                  Check-out Date *
                  {selectedCourse && selectedCourse.endDate && formData.checkOutDate > selectedCourse.endDate && (
                    <span style={{ color: '#B68235', fontSize: 12, marginLeft: 8 }}>
                      (Extended by {differenceInDays(new Date(formData.checkOutDate), new Date(selectedCourse.endDate))} days)
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  name="checkOutDate"
                  value={formData.checkOutDate}
                  onChange={handleInputChange}
                  min={formData.checkInDate ? format(addDays(new Date(formData.checkInDate), 1), 'yyyy-MM-dd') : ''}
                  required
                  style={{
                    width: '100%',
                    padding: 10,
                    border: '2px solid #1A3E2A',
                    borderRadius: 6,
                    fontSize: 14,
                    boxSizing: 'border-box'
                  }}
                />
                {selectedCourse && selectedCourse.endDate && (
                  <div style={{ fontSize: 12, color: '#5F6B61', marginTop: 4 }}>
                    Course ends: {format(new Date(selectedCourse.endDate), 'MMM dd, yyyy')}
                  </div>
                )}
              </div>
            </div>
            <div style={{
              marginTop: 12,
              padding: 10,
              backgroundColor: '#ffffff',
              borderRadius: 6,
              border: '1px solid #C4D4C8',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13
            }}>
              <span><BedDoubleIcon size="1em" aria-hidden="true" /></span>
              <strong>Total Stay:</strong>
              <span style={{ color: '#0F2619', fontWeight: 600 }}>
                {differenceInDays(new Date(formData.checkOutDate), new Date(formData.checkInDate))} nights
              </span>
              {selectedCourse && selectedCourse.startDate && selectedCourse.endDate && (
                <>
                  <span style={{ marginLeft: 8, color: '#5F6B61' }}>|</span>
                  <span style={{ color: '#5F6B61' }}>
                    Course: {differenceInDays(new Date(selectedCourse.endDate), new Date(selectedCourse.startDate))} nights
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Guest Count and Course Count for Private Rooms */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
          gap: 16, 
          marginBottom: 20,
          padding: 16,
          backgroundColor: '#F5F3EB',
          borderRadius: 8,
          border: '1px solid #E3DFD3'
        }}>
          <CounterInput
            label="Guest Count"
            value={formData.guestCount || 1}
            onChange={(value) => setFormData(prev => ({ ...prev, guestCount: value }))}
            min={1}
            max={10}
            helperText="Number of guests in the booking"
            style={{ width: '100%' }}
          />
          
          <CounterInput
            label={!selectedCourse ? "Course Count" : "Course Count"}
            value={formData.courseCount || 0}
            onChange={(value) => setFormData(prev => ({ ...prev, courseCount: value }))}
            min={selectedCourse ? 1 : 0}
            max={5}
            disabled={!selectedCourse} // Locked for staycation
            helperText={selectedCourse ? 'Number of course enrollments' : 'Locked at 0 for staycation'}
            style={{ width: '100%' }}
          />
          
          {/* Course count impact indicator */}
          {selectedCourse && formData.courseCount >= 1 && (
            <div style={{
              gridColumn: '1 / -1',
              marginTop: 8,
              padding: 12,
              backgroundColor: formData.courseCount > 1 ? '#F3E6CB' : '#F0F3EE',
              border: `1px solid ${formData.courseCount > 1 ? '#B68235' : '#1A3E2A'}`,
              borderRadius: 6,
              fontSize: 14
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}><LightbulbIcon size="1em" aria-hidden="true" /></span>
                <strong style={{ color: formData.courseCount > 1 ? '#684A1F' : '#122E20' }}>Course Cost Breakdown</strong>
              </div>
              <div style={{ color: formData.courseCount > 1 ? '#4F3818' : '#0B1D13', fontSize: 13 }}>
                Course fee per enrollment: ₹{selectedCourse.fee || 0}
                <br />
                {formData.courseCount > 1 ? (
                  <>Total course fees: ₹{((selectedCourse.fee || 0) * formData.courseCount).toFixed(2)} 
                  ({formData.courseCount} enrollments × ₹{selectedCourse.fee || 0})</>
                ) : (
                  <>Single course enrollment: ₹{selectedCourse.fee || 0}</>
                )}
              </div>
            </div>
          )}

          {/* Staycation indicator */}
          {!selectedCourse && (
            <div style={{
              gridColumn: '1 / -1',
              marginTop: 8,
              padding: 12,
              backgroundColor: '#F9EEEB',
              border: '1px solid #D48A7C',
              borderRadius: 6,
              fontSize: 14
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}><PalmtreeIcon size="1em" aria-hidden="true" /></span>
                <strong style={{ color: '#8E3628' }}>Staycation Mode</strong>
              </div>
              <div style={{ color: '#481B14', fontSize: 13 }}>
                No course selected - accommodation only. Course count is automatically set to 0.
                <br />
                <span style={{ fontWeight: 600 }}>Total course fees: ₹0</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ border: '1px solid #E3DFD3', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Guest Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <input placeholder="Full Name *" value={primaryGuest.name} onChange={(e) => handlePrimaryGuestChange('name', e.target.value)} style={mobileFieldStyle} />
            <input placeholder="Email *" type="email" value={primaryGuest.email} onChange={(e) => handlePrimaryGuestChange('email', e.target.value)} style={mobileFieldStyle} />
            <input placeholder="Phone" value={primaryGuest.phone} onChange={(e) => handlePrimaryGuestChange('phone', e.target.value)} style={mobileFieldStyle} />
            <select value={primaryGuest.gender} onChange={(e) => handlePrimaryGuestChange('gender', e.target.value)} style={mobileFieldStyle}>
              <option value="">Select Gender *</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div style={{ color: '#5F6B61', fontSize: 12, marginTop: 6 }}>These details will apply to all selected rooms.</div>
        </div>

        {/* Payment Status & Notes */}
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Payment Status</label>
            <select
              name="paymentStatus"
              value={formData.paymentStatus}
              onChange={handleInputChange}
              style={{ ...mobileFieldStyle, padding: 10 }}
            >
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Paid</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          {addons.length > 0 && (
            <div>
              <h4 style={{ margin: '12px 0 8px 0' }}>Add-on Services (Optional)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px,1fr))', gap: 8 }}>
                {addons.map(addon => (
                  <label key={addon.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, border: '1px solid #E3DFD3', borderRadius: 8, background: formData.addons.includes(addon.id) ? '#E1E9E2' : '#F5F3EB', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.addons.includes(addon.id)} onChange={(e) => handleAddonChange(addon.id, e.target.checked)} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{addon.name}</span>
                      <span style={{ color: '#163626' }}>₹{addon.price}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Special Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any special notes or requirements" style={{ ...mobileFieldStyle, padding: 10, resize: 'vertical' }} />
          </div>
        </div>
      </div>

      {/* Pricing Sidebar */}
      <aside style={{ position: isMobile ? 'static' : 'sticky', top: 16, alignSelf: 'start' }}>
        <div style={{ 
          border: isOMGExperienceMode ? '2px solid #A9613C' : '1px solid #E3DFD3', 
          borderRadius: 12, 
          background: isOMGExperienceMode ? '#F8F0EA' : 'white', 
          padding: 16 
        }}>
          {/* OMG Experience Badge */}
          {isOMGExperienceMode && (
            <div style={{
              backgroundColor: '#A9613C',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: '700'
            }}>
              <span style={{ fontSize: '16px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
              OMG Experience Active
              <span style={{ marginLeft: 'auto', fontSize: '11px', opacity: 0.9 }}>+20%</span>
            </div>
          )}

          <h3 style={{ 
            marginTop: 0, 
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: isOMGExperienceMode ? '#663923' : 'inherit',
            ...(calculatingPrice ? { 
              animation: 'pulse 1s ease-in-out infinite',
              color: '#B68235'
            } : {})
          }}>
            Price Summary
            {calculatingPrice && <span style={{ fontSize: 14 }}><RefreshCwIcon size="1em" aria-hidden="true" /></span>}
          </h3>
          {calculatingPrice ? (
            <div style={{ color: '#5F6B61' }}>Calculating…</div>
          ) : totalPricing.individualBookings.length === 0 ? (
            <div style={{ color: '#5F6B61' }}>No pricing yet</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {totalPricing.individualBookings.map((item, idx) => (
                <div key={`price-${idx}`} style={{ 
                  border: item.pricing?.isOMGPricing ? '2px solid #A9613C' : '1px solid #E3DFD3', 
                  borderRadius: 8, 
                  padding: 10,
                  backgroundColor: item.pricing?.isOMGPricing ? '#F8F0EA' : 'white'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#1A3E2A', fontSize: 14 }}>
                    {item.bed ? `Room ${item.room.roomNumber} • Bed ${item.bed.bedNumber}` : `Room ${item.room.roomNumber}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#5F6B61', marginBottom: 8 }}>
                    {item.room.roomType?.name || 'Room'}
                  </div>
                  <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
                    {/* Show base price calculation */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#36433B' }}>
                      <span>Base Rate:</span>
                      <span>₹{item.pricing?.pricePerNight || 0}/night</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#5F6B61', fontSize: 12 }}>
                      <span>Nights:</span>
                      <span>× {item.pricing?.nights || 0}</span>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#36433B', fontWeight: 600, paddingTop: 4, borderTop: '1px solid #E3DFD3' }}>
                      <span>Base Total:</span>
                      <span>₹{Math.round(item.pricing?.basePrice || 0)}</span>
                    </div>
                    
                    {/* Show OMG markup if applicable */}
                    {item.pricing?.omgMarkup > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#A9613C', fontWeight: 700, marginTop: 4 }}>
                        <span>OMG Markup:</span>
                        <span>+₹{Math.round(item.pricing.omgMarkup)}</span>
                      </div>
                    )}
                    
                    {/* Add-ons if any */}
                    {item.pricing?.addOnsPrice > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#5F6B61', marginTop: 4 }}>
                        <span>Add-ons:</span>
                        <span>+₹{Math.round(item.pricing.addOnsPrice)}</span>
                      </div>
                    )}
                    
                    {/* Subtotal */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      fontWeight: 700, 
                      marginTop: 8,
                      paddingTop: 8,
                      borderTop: '2px solid ' + (item.pricing?.isOMGPricing ? '#A9613C' : '#163626'),
                      color: item.pricing?.isOMGPricing ? '#A9613C' : '#163626',
                      fontSize: 15
                    }}>
                      <span>Room Total:</span>
                      <span>₹{Math.round(item.pricing?.accommodationPrice || 0)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div style={{ borderTop: '1px solid #E3DFD3', paddingTop: 8 }}>
                {/* Show total OMG markup if any room has OMG pricing */}
                {totalPricing.hasOMGMarkup && totalPricing.totalOMGMarkup > 0 && (
                  <div style={{
                    backgroundColor: '#F1E0D4',
                    border: '1px solid #A9613C',
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 12
                  }}>
                    <div style={{ fontSize: 13, color: '#663923', fontWeight: 700, marginBottom: 6 }}>
                      OMG Experience Breakdown
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#975534', marginBottom: 2 }}>
                      <span>Total Base Price:</span>
                      <span>₹{Math.round(totalPricing.totalBasePrice || 0)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#A9613C', fontWeight: 700 }}>
                      <span>Total OMG Markup:</span>
                      <span>+₹{Math.round(totalPricing.totalOMGMarkup || 0)}</span>
                    </div>
                    {totalPricing.markupInfo && (
                      <div style={{ fontSize: 11, color: '#975534', marginTop: 6, fontStyle: 'italic' }}>
                        {totalPricing.markupInfo}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show accommodation total */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: 4, fontWeight: 600 }}>
                  <span>Accommodation Total:</span>
                  <span>₹{Math.round(totalPricing.totalAccommodation || 0)}</span>
                </div>
                
                {/* Show course fee breakdown */}
                {selectedCourse && formData.courseCount > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#975534', marginBottom: 4 }}>
                      <span>Course Fee ({formData.courseCount}× ₹{selectedCourse.fee || 0})</span>
                      <span>₹{(totalPricing.courseFee || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}

                {/* Show staycation mode indicator */}
                {!selectedCourse && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#5F6B61', marginBottom: 4 }}>
                      <span>Course Fee (Staycation)</span>
                      <span>₹0.00</span>
                    </div>
                  </>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '16px' }}>
                  <span>Total ({selectedRooms.length} room{selectedRooms.length !== 1 ? 's' : ''})</span>
                  <span style={{ 
                    color: '#163626',
                    transition: 'all 0.3s ease',
                    ...(totalAmountChanged ? {
                      backgroundColor: '#E1E9E2',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      transform: 'scale(1.05)'
                    } : {})
                  }}>₹{(totalPricing.totalAmount || 0).toFixed(2)}</span>
                </div>

                {/* Extended stay indicator */}
                {selectedCourse && selectedCourse.startDate && selectedCourse.endDate && (
                  (() => {
                    const courseNights = differenceInDays(new Date(selectedCourse.endDate), new Date(selectedCourse.startDate));
                    const bookingNights = differenceInDays(new Date(formData.checkOutDate), new Date(formData.checkInDate));
                    const extraNights = bookingNights - courseNights;
                    const isExtended = extraNights > 0;
                    
                    if (isExtended) {
                      return (
                        <div style={{
                          marginTop: 8,
                          padding: 10,
                          backgroundColor: '#F3E6CB',
                          border: '1px solid #B68235',
                          borderRadius: 6,
                          fontSize: 12
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 16 }}><CalendarIcon size="1em" aria-hidden="true" /></span>
                            <strong style={{ color: '#684A1F' }}>Extended Stay</strong>
                          </div>
                          <div style={{ color: '#4F3818' }}>
                            Course: {courseNights} nights
                            <br />
                            Your booking: {bookingNights} nights
                            <br />
                            <strong style={{ color: '#B68235' }}>+{extraNights} extra night{extraNights !== 1 ? 's' : ''}</strong>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()
                )}
                
                {/* Show real-time update indicator */}
                {calculatingPrice && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#B68235', 
                    fontStyle: 'italic',
                    marginTop: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    <div style={{ 
                      width: 12, 
                      height: 12, 
                      border: '2px solid #B68235', 
                      borderTop: '2px solid transparent', 
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Updating price...
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                disabled={loading || selectedRooms.length === 0}
                style={{
                  width: '100%',
                  backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  padding: '12px 16px',
                  borderRadius: 8,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 800
                }}
              >
                {loading ? 'Creating…' : `Create ${selectedRooms.length} Booking${selectedRooms.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );

  return (
    <div className="staff-reservation">
      {/* Super Admin Mode Toggle */}
      {isSuperAdmin && (
        <div style={{ 
          marginBottom: '24px', 
          padding: isMobile ? '16px' : '20px', 
          border: '2px solid #B68235', 
          borderRadius: '12px', 
          backgroundColor: '#F3E6CB' 
        }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '24px' }}><ShieldIcon size="1em" aria-hidden="true" /></span>
                <h3 style={{ margin: 0, color: '#684A1F' }}>Super Admin Features</h3>
              </div>
              <p style={{ margin: 0, color: '#4F3818', fontSize: '14px' }}>
                {isPastDateMode 
                  ? 'You are in Past Date Booking mode - can create bookings for historical dates'
                  : 'You are in Regular Booking mode - standard future date bookings only'
                }
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F' }}>
                  {isPastDateMode ? 'Past Dates' : 'Future Dates'}
                </span>
                <div style={{ 
                  position: 'relative',
                  width: '60px',
                  height: '30px',
                  backgroundColor: isPastDateMode ? '#8E3628' : '#1A3E2A',
                  borderRadius: '15px',
                  transition: 'background-color 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isPastDateMode}
                    onChange={(e) => setIsPastDateMode(e.target.checked)}
                    style={{ opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer' }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: isPastDateMode ? '33px' : '3px',
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'left 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F' }}>
                  Past Date Booking
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Render appropriate component based on mode */}
      {isSuperAdmin && isPastDateMode ? (
        <SuperAdminPastDateReservation 
          user={user} 
          onReservationCreated={onReservationCreated} 
        />
      ) : (
        <>
          {currentStep === 'course-selection' && renderCourseSelection()}
          {currentStep === 'room-selection' && renderRoomSelection()}
          {currentStep === 'guest-details' && renderGuestDetails()}
        </>
      )}
    </div>
  );
}

export default StaffReservation;